import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  GripVertical,
  Plus,
  Trash2,
  Shuffle,
  ArrowUpDown,
  Users,
  Car,
  AlertCircle,
  CheckCircle2,
  Loader2,
  X,
  Dices,
  UserPlus,
  Info,
  Send
} from 'lucide-react';
import SendLineupModal from '../../components/SendLineupModal';
import { useSeries } from '../../App';
import { useApi } from '../../contexts/ApiContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Helper to determine race type category
function getRaceTypeCategory(raceTypeName) {
  if (!raceTypeName) return 'standard';
  const name = raceTypeName.toLowerCase();

  if (name.includes('hot lap') || name.includes('hotlap')) {
    return 'hotlaps';
  }
  if (name.includes('qualifying') || name.includes('qual')) {
    return 'qualifying';
  }
  return 'standard';
}

// Sortable Lineup Row Component
function SortableLineupRow({ driver, index, onRemove, onTransponderChange, showDragHandle = true, showPosition = true, isQualifying = false }) {
  const [editingTransponder, setEditingTransponder] = useState(false);
  const [transponderValue, setTransponderValue] = useState(driver.transponder || '');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: driver.competitor_ID, disabled: !showDragHandle });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const pillNumber = index + 1;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 px-3 py-2.5 border-b border-slate-200 last:border-b-0 ${
        isDragging ? 'bg-slate-100' : 'bg-white hover:bg-slate-50'
      } transition-colors`}
    >
      {/* Drag Handle */}
      {showDragHandle ? (
        <button
          {...attributes}
          {...listeners}
          className="p-0.5 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      ) : (
        <div className="w-5" /> // Spacer when no drag handle
      )}

      {/* Position / Pill Number */}
      {showPosition ? (
        isQualifying ? (
          // Qualifying: Show pill-style badge
          <div className="flex flex-col items-center gap-0.5">
            <div className="w-8 h-7 rounded bg-purple-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
              {pillNumber}
            </div>
            <span className="text-2xs font-medium text-purple-600 uppercase tracking-wide">Pill</span>
          </div>
        ) : (
          // Standard: Show position circle
          <div className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold text-sm">
            {pillNumber}
          </div>
        )
      ) : (
        <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center">
          <Car className="w-4 h-4" />
        </div>
      )}

      {/* Car Number */}
      <div className="w-12 h-8 rounded bg-white border-2 border-slate-800 flex items-center justify-center">
        <span className="font-mono font-bold text-sm text-slate-800">
          {driver.car_number || '-'}
        </span>
      </div>

      {/* Driver Info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate">
          {driver.driver_name}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {driver.hometown || 'No hometown'}
        </div>
      </div>

      {/* Transponder */}
      <div className="flex-shrink-0 w-24">
        {editingTransponder ? (
          <input
            type="text"
            value={transponderValue}
            onChange={(e) => setTransponderValue(e.target.value)}
            onBlur={() => {
              onTransponderChange(driver.competitor_ID, transponderValue);
              setEditingTransponder(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onTransponderChange(driver.competitor_ID, transponderValue);
                setEditingTransponder(false);
              }
              if (e.key === 'Escape') {
                setTransponderValue(driver.transponder || '');
                setEditingTransponder(false);
              }
            }}
            className="w-full px-2 py-1 text-xs font-mono bg-white border border-wrg-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-wrg-blue-500"
            autoFocus
            maxLength={20}
          />
        ) : (
          <button
            onClick={() => setEditingTransponder(true)}
            className="w-full px-2 py-1 text-xs font-mono text-slate-600 bg-slate-100 hover:bg-slate-200 rounded transition-colors text-left truncate"
            title="Click to edit transponder"
          >
            {driver.transponder || '-'}
          </button>
        )}
      </div>

      {/* Remove Button */}
      <button
        onClick={() => onRemove(driver.competitor_ID)}
        className="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
        title="Remove from lineup"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// Simple Row for Hot Laps (no drag, no position)
function SimpleDriverRow({ driver, onRemove, onTransponderChange }) {
  const [editingTransponder, setEditingTransponder] = useState(false);
  const [transponderValue, setTransponderValue] = useState(driver.transponder || '');

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border-b border-slate-200 last:border-b-0 bg-white hover:bg-slate-50 transition-colors">
      {/* Car Number */}
      <div className="w-12 h-8 rounded bg-white border-2 border-slate-800 flex items-center justify-center">
        <span className="font-mono font-bold text-sm text-slate-800">
          {driver.car_number || '-'}
        </span>
      </div>

      {/* Driver Info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate">
          {driver.driver_name}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {driver.hometown || 'No hometown'}
        </div>
      </div>

      {/* Transponder */}
      <div className="flex-shrink-0 w-24">
        {editingTransponder ? (
          <input
            type="text"
            value={transponderValue}
            onChange={(e) => setTransponderValue(e.target.value)}
            onBlur={() => {
              onTransponderChange(driver.competitor_ID, transponderValue);
              setEditingTransponder(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onTransponderChange(driver.competitor_ID, transponderValue);
                setEditingTransponder(false);
              }
              if (e.key === 'Escape') {
                setTransponderValue(driver.transponder || '');
                setEditingTransponder(false);
              }
            }}
            className="w-full px-2 py-1 text-xs font-mono bg-white border border-wrg-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-wrg-blue-500"
            autoFocus
            maxLength={20}
          />
        ) : (
          <button
            onClick={() => setEditingTransponder(true)}
            className="w-full px-2 py-1 text-xs font-mono text-slate-600 bg-slate-100 hover:bg-slate-200 rounded transition-colors text-left truncate"
            title="Click to edit transponder"
          >
            {driver.transponder || '-'}
          </button>
        )}
      </div>

      {/* Remove Button */}
      <button
        onClick={() => onRemove(driver.competitor_ID)}
        className="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
        title="Remove from hot laps"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// Available Driver Card
function AvailableDriverCard({ driver, onAdd }) {
  return (
    <div className="flex items-center gap-2.5 p-2.5 bg-white rounded border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all">
      {/* Car Number */}
      <div className="w-10 h-7 rounded bg-white border-2 border-slate-700 flex items-center justify-center">
        <span className="font-mono font-bold text-xs text-slate-800">
          {driver.car_number || '-'}
        </span>
      </div>

      {/* Driver Info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800 truncate">
          {driver.driver_name}
        </div>
        <div className="text-2xs text-slate-500 truncate">
          {driver.hometown || 'N/A'}
        </div>
      </div>

      {/* Add Button */}
      <button
        onClick={() => onAdd(driver.entrant_ID)}
        className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded transition-colors"
        title="Add to lineup"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

// Add Driver Modal
function AddDriverModal({ isOpen, onClose, drivers, onAdd, onAddAll }) {
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen) return null;

  const filteredDrivers = drivers.filter(d =>
    d.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.car_number?.toString().includes(searchTerm)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg border border-slate-200 w-full max-w-sm max-h-[80vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-200">
          <h3 className="text-md font-semibold text-slate-800">Add Driver to Lineup</h3>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-slate-200">
          <input
            type="text"
            placeholder="Search by name or car number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-wrg-blue-500 focus:ring-1 focus:ring-wrg-blue-500"
            autoFocus
          />
        </div>

        {/* Add All Button */}
        {onAddAll && drivers.length > 0 && (
          <div className="px-3 pt-3">
            <button
              onClick={() => {
                onAddAll();
                onClose();
              }}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 transition-colors"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Add All {drivers.length} Drivers
            </button>
          </div>
        )}

        {/* Driver List */}
        <div className="max-h-80 overflow-y-auto p-3 space-y-1.5">
          {filteredDrivers.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">
              {drivers.length === 0 ? 'No available drivers' : 'No matching drivers'}
            </div>
          ) : (
            filteredDrivers.map(driver => (
              <button
                key={driver.entrant_ID}
                onClick={() => {
                  onAdd(driver.entrant_ID);
                  onClose();
                }}
                className="w-full flex items-center gap-2.5 p-2.5 bg-slate-50 rounded border border-slate-200 hover:border-wrg-blue-500 hover:bg-wrg-blue-50 transition-colors text-left"
              >
                <div className="w-10 h-7 rounded bg-white border-2 border-slate-700 flex items-center justify-center">
                  <span className="font-mono font-bold text-xs text-slate-800">
                    {driver.car_number || '-'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {driver.driver_name}
                  </div>
                  <div className="text-2xs text-slate-500 truncate">
                    {driver.hometown || 'N/A'}
                  </div>
                </div>
                <Plus className="w-4 h-4 text-emerald-600" />
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function RaceLineup() {
  const { raceId } = useParams();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const currentSeries = useSeries();
  const api = useApi();

  const [race, setRace] = useState(null);
  const [lineup, setLineup] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { isLoading: loading, refetch: loadRaceData } = useQuery({
    queryKey: ['race-lineup', raceId],
    queryFn: async () => {
      const [raceResult, lineupResult, availableResult] = await Promise.all([
        api.races.getById(raceId),
        api.races.getLineup(raceId),
        api.races.getAvailableDrivers(raceId)
      ]);

      if (raceResult.success) {
        setRace(raceResult.data?.race || raceResult.data);
      } else {
        setError(raceResult.message || 'Failed to load race details');
      }

      if (lineupResult.success) {
        setLineup(lineupResult.data?.lineup || []);
      }

      if (availableResult.success) {
        setAvailableDrivers(availableResult.data?.drivers || []);
      }

      return { race: raceResult.data, lineup: lineupResult.data, available: availableResult.data };
    },
    enabled: !!raceId,
  });

  // Determine race type category
  const raceTypeCategory = getRaceTypeCategory(race?.race_type);
  const isHotLaps = raceTypeCategory === 'hotlaps';
  const isQualifying = raceTypeCategory === 'qualifying';

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setLineup((items) => {
      const oldIndex = items.findIndex((item) => item.competitor_ID === active.id);
      const newIndex = items.findIndex((item) => item.competitor_ID === over.id);

      if (oldIndex === -1 || newIndex === -1) return items;

      const newLineup = arrayMove(items, oldIndex, newIndex);

      // Update positions
      return newLineup.map((item, index) => ({
        ...item,
        race_position: index + 1,
      }));
    });

    setHasUnsavedChanges(true);
  };

  // Add driver to lineup
  const handleAddDriver = async (entrantId) => {
    try {
      setError('');
      const result = await api.races.addToLineup(raceId, entrantId);

      if (result.success) {
        await loadRaceData();
        setSuccess('Driver added to lineup');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to add driver');
      }
    } catch (err) {
      console.error('Error adding driver:', err);
      setError('Failed to add driver to lineup');
    }
  };

  // Add all available drivers to lineup (for Hot Laps)
  const handleAddAllDrivers = async () => {
    try {
      setError('');
      setSaving(true);

      // Add all available drivers one by one
      for (const driver of availableDrivers) {
        await api.races.addToLineup(raceId, driver.entrant_ID);
      }

      await loadRaceData();
      setSuccess(`Added ${availableDrivers.length} drivers to lineup`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding all drivers:', err);
      setError('Failed to add all drivers');
    } finally {
      setSaving(false);
    }
  };

  // Remove driver from lineup
  const handleRemoveDriver = async (competitorId) => {
    if (!window.confirm('Remove this driver from the lineup?')) return;

    try {
      setError('');
      const result = await api.races.removeFromLineup(raceId, competitorId);

      if (result.success) {
        await loadRaceData();
        setSuccess('Driver removed from lineup');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to remove driver');
      }
    } catch (err) {
      console.error('Error removing driver:', err);
      setError('Failed to remove driver');
    }
  };

  // Update transponder
  const handleTransponderChange = async (competitorId, transponder) => {
    try {
      setError('');
      const result = await api.races.updateTransponder(raceId, competitorId, transponder);

      if (result.success) {
        setLineup(prev => prev.map(item =>
          item.competitor_ID === competitorId
            ? { ...item, transponder }
            : item
        ));
      } else {
        setError(result.message || 'Failed to update transponder');
      }
    } catch (err) {
      console.error('Error updating transponder:', err);
      setError('Failed to update transponder');
    }
  };

  // Save lineup positions
  const handleSaveLineup = async () => {
    try {
      setSaving(true);
      setError('');

      const positions = lineup.map((item, index) => ({
        competitor_id: item.competitor_ID,
        race_position: index + 1,
      }));

      const result = await api.races.updateLineupPositions(raceId, positions);

      if (result.success) {
        setSuccess('Lineup saved successfully');
        setHasUnsavedChanges(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to save lineup');
      }
    } catch (err) {
      console.error('Error saving lineup:', err);
      setError('Failed to save lineup');
    } finally {
      setSaving(false);
    }
  };

  // Pill Draw - Randomize qualifying order
  const handlePillDraw = () => {
    if (!window.confirm('Perform pill draw to randomize qualifying order?')) return;

    const shuffled = [...lineup].sort(() => Math.random() - 0.5);
    const updatedLineup = shuffled.map((item, index) => ({
      ...item,
      race_position: index + 1,
    }));

    setLineup(updatedLineup);
    setHasUnsavedChanges(true);
    setSuccess('Pill draw complete - qualifying order randomized');
    setTimeout(() => setSuccess(''), 3000);
  };

  // Randomize lineup
  const handleRandomize = () => {
    if (!window.confirm('Randomize the starting lineup?')) return;

    const shuffled = [...lineup].sort(() => Math.random() - 0.5);
    const updatedLineup = shuffled.map((item, index) => ({
      ...item,
      race_position: index + 1,
    }));

    setLineup(updatedLineup);
    setHasUnsavedChanges(true);
  };

  // Invert lineup
  const handleInvert = () => {
    if (!window.confirm('Invert the starting lineup?')) return;

    const inverted = [...lineup].reverse();
    const updatedLineup = inverted.map((item, index) => ({
      ...item,
      race_position: index + 1,
    }));

    setLineup(updatedLineup);
    setHasUnsavedChanges(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-wrg-blue-600 animate-spin" />
          <span className="text-slate-600">Loading race data...</span>
        </div>
      </div>
    );
  }

  // Error state (no race loaded)
  if (!race && error) {
    return (
      <div className="animate-fade-in">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </button>
          <h1 className="text-lg font-semibold text-slate-800">Race Lineup</h1>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-1.5 bg-slate-800 text-white text-sm rounded hover:bg-slate-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Get title and description based on race type
  const getPageInfo = () => {
    if (isHotLaps) {
      return {
        title: 'Hot Laps Participants',
        subtitle: 'All entrants participate in hot laps',
        emptyTitle: 'No Drivers in Hot Laps',
        emptyMessage: 'Add all event entrants to participate in hot laps.',
        addButtonText: 'Add All Entrants'
      };
    }
    if (isQualifying) {
      return {
        title: 'Qualifying Order',
        subtitle: 'Order determined by pill draw',
        emptyTitle: 'No Drivers in Qualifying',
        emptyMessage: 'Add drivers and use Pill Draw to randomize the qualifying order.',
        addButtonText: 'Add Drivers'
      };
    }
    return {
      title: 'Starting Lineup',
      subtitle: 'Drag rows to reorder positions',
      emptyTitle: 'No Drivers in Lineup',
      emptyMessage: 'Add drivers from the available list or use the Add Driver button.',
      addButtonText: 'Add Drivers'
    };
  };

  const pageInfo = getPageInfo();

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 rounded hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </button>
            <div className="w-8 h-8 rounded bg-wrg-blue-50 flex items-center justify-center">
              <Users className="w-4 h-4 text-wrg-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h1 className="text-lg font-semibold text-slate-800">{pageInfo.title}</h1>
                {(isHotLaps || isQualifying) && (
                  <span className={`px-1.5 py-0.5 text-2xs font-medium rounded ${
                    isHotLaps ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {isHotLaps ? 'Hot Laps' : 'Qualifying'}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">
                {race?.name} • {race?.race_type}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                Unsaved
              </span>
            )}
            <button
              onClick={() => setSendModalOpen(true)}
              disabled={lineup.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-sm rounded hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              Send
            </button>
            <button
              onClick={handleSaveLineup}
              disabled={saving || lineup.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-wrg-blue-600 text-white text-sm rounded hover:bg-wrg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  Save {isHotLaps ? 'Participants' : 'Lineup'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Race Type Info Banner */}
        {(isHotLaps || isQualifying) && (
          <div className={`mb-3 p-3 rounded flex items-start gap-2.5 ${
            isHotLaps ? 'bg-orange-50 border border-orange-200' : 'bg-purple-50 border border-purple-200'
          }`}>
            <Info className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isHotLaps ? 'text-orange-600' : 'text-purple-600'}`} />
            <div>
              <p className={`text-sm font-medium ${isHotLaps ? 'text-orange-800' : 'text-purple-800'}`}>
                {isHotLaps ? 'Hot Laps Session' : 'Qualifying Session'}
              </p>
              <p className={`text-xs ${isHotLaps ? 'text-orange-600' : 'text-purple-600'}`}>
                {isHotLaps
                  ? 'All entrants should participate. Use "Add All Entrants" to quickly add everyone.'
                  : 'Use "Pill Draw" to randomize the qualifying order.'
                }
              </p>
            </div>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded flex items-center gap-2.5 text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm flex-1">{error}</span>
            <button
              onClick={() => setError('')}
              className="text-red-500 hover:text-red-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded flex items-center gap-2.5 text-emerald-700">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm flex-1">{success}</span>
            <button
              onClick={() => setSuccess('')}
              className="text-emerald-500 hover:text-emerald-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Available Drivers Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="p-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-slate-800">Available Drivers</h2>
                  <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">
                    {availableDrivers.length}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Click + to add to {isHotLaps ? 'session' : 'lineup'}</p>
              </div>

              {/* Add All Button for Hot Laps */}
              {isHotLaps && availableDrivers.length > 0 && (
                <div className="p-3 border-b border-slate-200 bg-slate-50">
                  <button
                    onClick={handleAddAllDrivers}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    Add All Entrants
                  </button>
                </div>
              )}

              <div className="p-3">
                {availableDrivers.length === 0 ? (
                  <div className="text-center py-6">
                    <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-xs">No available drivers</p>
                    <p className="text-slate-400 text-2xs mt-0.5">All drivers are in the {isHotLaps ? 'session' : 'lineup'}</p>
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-[calc(100vh-320px)] overflow-y-auto">
                    {availableDrivers.map(driver => (
                      <AvailableDriverCard
                        key={driver.entrant_ID}
                        driver={driver}
                        onAdd={handleAddDriver}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Lineup Table */}
          <div className="lg:col-span-3 space-y-3">
            {/* Quick Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setAddModalOpen(true)}
                disabled={availableDrivers.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-sm rounded hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Driver
              </button>

              {/* Pill Draw button for Qualifying */}
              {isQualifying && (
                <button
                  onClick={handlePillDraw}
                  disabled={lineup.length === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                >
                  <Dices className="w-3.5 h-3.5" />
                  Pill Draw
                </button>
              )}

              {/* Standard actions for non-Hot Laps */}
              {!isHotLaps && (
                <>
                  <button
                    onClick={handleRandomize}
                    disabled={lineup.length === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-sm rounded hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                  >
                    <Shuffle className="w-3.5 h-3.5" />
                    Randomize
                  </button>
                  <button
                    onClick={handleInvert}
                    disabled={lineup.length === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-sm rounded hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowUpDown className="w-3.5 h-3.5" />
                    Invert
                  </button>
                </>
              )}

              <div className="ml-auto text-xs text-slate-500">
                {lineup.length} driver{lineup.length !== 1 ? 's' : ''} {isHotLaps ? 'participating' : 'in lineup'}
              </div>
            </div>

            {/* Lineup Card */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="p-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-800">{pageInfo.title}</h2>
                    <p className="text-xs text-slate-500">{pageInfo.subtitle}</p>
                  </div>
                  {lineup.length > 0 && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-wrg-blue-100 text-wrg-blue-700 text-xs font-medium rounded-full">
                      <Car className="w-3 h-3" />
                      {lineup.length} cars
                    </span>
                  )}
                </div>
              </div>

              {lineup.length === 0 ? (
                <div className="p-8 text-center">
                  <Car className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-md font-medium text-slate-700 mb-1.5">{pageInfo.emptyTitle}</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    {pageInfo.emptyMessage}
                  </p>
                  {isHotLaps && availableDrivers.length > 0 ? (
                    <button
                      onClick={handleAddAllDrivers}
                      disabled={saving}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors mx-auto"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Add All Entrants
                    </button>
                  ) : (
                    <button
                      onClick={() => setAddModalOpen(true)}
                      disabled={availableDrivers.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-wrg-blue-600 text-white text-sm rounded hover:bg-wrg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors mx-auto"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {availableDrivers.length === 0 ? 'No Available Drivers' : pageInfo.addButtonText}
                    </button>
                  )}
                </div>
              ) : isHotLaps ? (
                // Hot Laps - Simple list, no drag, no positions
                <div>
                  {lineup.map((driver) => (
                    <SimpleDriverRow
                      key={driver.competitor_ID}
                      driver={driver}
                      onRemove={handleRemoveDriver}
                      onTransponderChange={handleTransponderChange}
                    />
                  ))}
                </div>
              ) : (
                // Standard/Qualifying - Drag and drop with positions
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  {/* Header */}
                  <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200 text-2xs font-medium text-slate-500 uppercase tracking-wider">
                    <div className="w-6" /> {/* Drag handle space */}
                    <div className="w-8 text-center">{isQualifying ? 'Order' : 'Pos'}</div>
                    <div className="w-12 text-center">Car</div>
                    <div className="flex-1">Driver</div>
                    <div className="w-24">Transp.</div>
                    <div className="w-8" /> {/* Actions space */}
                  </div>

                  {/* Lineup Rows */}
                  <SortableContext
                    items={lineup.map(d => d.competitor_ID)}
                    strategy={verticalListSortingStrategy}
                  >
                    {lineup.map((driver, index) => (
                      <SortableLineupRow
                        key={driver.competitor_ID}
                        driver={driver}
                        index={index}
                        onRemove={handleRemoveDriver}
                        onTransponderChange={handleTransponderChange}
                        showDragHandle={true}
                        showPosition={true}
                        isQualifying={isQualifying}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>
        </div>

      {/* Add Driver Modal */}
      <AddDriverModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        drivers={availableDrivers}
        onAdd={handleAddDriver}
        onAddAll={isHotLaps ? handleAddAllDrivers : null}
      />

      {/* Send Lineup Modal */}
      <SendLineupModal
        isOpen={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        raceId={raceId}
        raceName={race?.name || ''}
        lineupCount={lineup.length}
      />
    </div>
  );
}
